import React, { useState, useMemo } from "react";
import { useDashboardTheme } from "./themeHelper";

interface AllRepsPageProps {
  onBack: () => void;
  userData: any;
  rawMeetings?: any[];
  onRepClick?: (rep: any) => void;
}

function getInitials(name: string = "") {
  return name.trim().split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ["#6C63FF", "#FF6584", "#43C6AC", "#F7971E", "#56CCF2", "#BB6BD9", "#F2994A", "#27AE60"];
function avatarColor(name: string = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: avatarColor(name),
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 600, color: "#fff", flexShrink: 0,
    }}>
      {getInitials(name)}
    </div>
  );
}

function getScoreColor(score: number): string {
  if (score >= 90) return "#4ADE80";
  if (score >= 70) return "#A3E635";
  if (score >= 50) return "#FACC15";
  return "#F87171";
}

function getScoreBg(score: number): string {
  if (score >= 90) return "rgba(74,222,128,0.12)";
  if (score >= 70) return "rgba(163,230,53,0.14)";
  if (score >= 50) return "rgba(250,204,21,0.12)";
  return score > 0 ? "rgba(248,113,113,0.12)" : "rgba(255,255,255,0.05)";
}

function ScoreBadge({ score }: { score: number }) {
  const { isDark } = useDashboardTheme();
  const color = score > 0 ? getScoreColor(score) : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.25)");
  const bg = getScoreBg(score);
  return (
    <div style={{
      width: 44, height: 44, borderRadius: "50%",
      border: `2px solid ${color}`, background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 13, fontWeight: 700, color: score > 0 ? color : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)"),
    }}>
      {score > 0 ? Math.round(score) : "—"}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const { isDark } = useDashboardTheme();
  const darkMap: Record<string, { bg: string; color: string; label: string }> = {
    SUPER_ADMIN: { bg: "rgba(108,99,255,0.2)", color: "#A78BFA", label: "Super Admin" },
    ADMIN: { bg: "rgba(6,182,212,0.15)", color: "#67E8F9", label: "Admin" },
    MANAGER: { bg: "rgba(251,191,36,0.15)", color: "#FDE68A", label: "Manager" },
    USER: { bg: "rgba(74,222,128,0.12)", color: "#86EFAC", label: "User" },
  };
  const lightMap: Record<string, { bg: string; color: string; label: string }> = {
    SUPER_ADMIN: { bg: "rgba(108,99,255,0.15)", color: "#6D28D9", label: "Super Admin" },
    ADMIN: { bg: "rgba(6,182,212,0.15)", color: "#0891B2", label: "Admin" },
    MANAGER: { bg: "rgba(251,191,36,0.15)", color: "#B45309", label: "Manager" },
    USER: { bg: "rgba(34,197,94,0.15)", color: "#15803D", label: "User" },
  };
  const map = isDark ? darkMap : lightMap;
  const s = map[role?.toUpperCase()] || { bg: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.65)", label: role || "User" };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function avg(arr: number[]): number {
  const v = arr.filter((x) => typeof x === "number" && !isNaN(x) && x > 0);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0;
}

function ArrowBtn({ onClick, hover }: { onClick: () => void; hover: boolean }) {
  const { isDark } = useDashboardTheme();
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        width: 32, height: 32, borderRadius: 8,
        background: hover 
          ? (isDark ? "rgba(108,99,255,0.3)" : "rgba(108,99,255,0.2)") 
          : (isDark ? "rgba(108,99,255,0.12)" : "rgba(108,99,255,0.06)"),
        border: "1px solid rgba(108,99,255,0.3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, transition: "all 0.2s",
        color: isDark ? "#A78BFA" : "#5B21B6",
      }}
    >
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
      </svg>
    </button>
  );
}

export default function AllRepsPage({ onBack, userData, rawMeetings = [], onRepClick }: AllRepsPageProps) {
  const { colors } = useDashboardTheme();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("score");
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const reps: any[] = useMemo(() => {
    const d = userData?.data || userData || {};
    const pitches = d.tenantPitchMeta?.items || [];
    const scenarios = d.tenantScenarios || [];
    const userSummary = d.userPitchSummary || [];
    const allUsers = d.tenantUserList?.items || [];

    const getUserId = (item: any) =>
      item?.userId ||
      item?.user?.userId ||
      item?.User?.userId ||
      item?.user?.id ||
      item?.User?.id ||
      item?.createdBy ||
      item?.ownerId ||
      item?.user_id ||
      item?.owner_id ||
      "";

    const isPracticeSession = (m: any) => {
      const type = String(m.meetingType || m.otherDetails?.meetingType || '').toLowerCase();
      return m.isScenario === true || type === 'scenario';
    };

    const userScoresMap: Record<string, number[]> = {};
    const addScore = (uid: string, score: number) => {
      if (!uid || score <= 0) return;
      if (!userScoresMap[uid]) userScoresMap[uid] = [];
      userScoresMap[uid].push(score);
    };

    pitches.forEach((p: any) => {
      const uid = getUserId(p);
      addScore(uid, Number(p.overallPercentage || p.score || 0));
    });

    scenarios.forEach((s: any) => {
      const uid = getUserId(s);
      addScore(uid, Number(s.overallScore || s.score || 0));
    });

    (rawMeetings || []).forEach((m: any) => {
      const uid = getUserId(m);
      addScore(uid, Number(m.overallPercentage || m.score || 0));
    });

    const meetingMap: Record<string, any[]> = {};
    const practiceMap: Record<string, any[]> = {};
    
    (rawMeetings || []).forEach((m: any) => {
      const uid = getUserId(m);
      if (!uid) return;
      if (isPracticeSession(m)) {
        if (!practiceMap[uid]) practiceMap[uid] = [];
        practiceMap[uid].push(m);
      } else {
        if (!meetingMap[uid]) meetingMap[uid] = [];
        meetingMap[uid].push(m);
      }
    });

    const userDetailMap: Record<string, any> = {};
    allUsers.forEach((u: any) => { userDetailMap[u.id] = u; });

    return userSummary.map((u: any) => {
      const uid = u.userId || getUserId(u);
      const detail = userDetailMap[uid] || {};
      const scores = userScoresMap[uid] || [];
      const meetingsCount = meetingMap[uid]?.length || 0;
      const practiceCount = practiceMap[uid]?.length || 0;

      return {
        ...u,
        userId: uid,
        email: u.email || detail.email || "—",
        role: u.role || detail.role || "USER",
        status: detail.status || (detail.status === false ? "inactive" : "active"),
        avgScore: Math.round(avg(scores)),
        pitchCount: (u.pitchCount || 0) + meetingsCount,
        practiceSessionCount: (u.practiceSessionCount || 0) + practiceCount,
      };
    });
  }, [userData, rawMeetings]);

  const filtered: any[] = useMemo(() => {
    return reps
      .filter((r: any) => {
        const matchSearch = (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
          (r.email || "").toLowerCase().includes(search.toLowerCase());
        const matchRole = roleFilter === "ALL" || (r.role || "").toUpperCase() === roleFilter.toUpperCase();
        return matchSearch && matchRole;
      })
      .sort((a: any, b: any) => {
        if (sortBy === "score") return b.avgScore - a.avgScore;
        if (sortBy === "pitches") return b.pitchCount - a.pitchCount;
        if (sortBy === "practice") return b.practiceSessionCount - a.practiceSessionCount;
        if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
        return 0;
      });
  }, [reps, search, roleFilter, sortBy]);

  const styles: Record<string, React.CSSProperties> = {
    root: { minHeight: "100vh", background: colors.bg, color: colors.text, fontFamily: "'Inter', -apple-system, sans-serif", padding: "28px 36px" },
    header: { display: "flex", alignItems: "center", gap: 16, marginBottom: 28 },
    backBtn: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 14px", color: colors.textMuted, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
    controls: { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" as const },
    input: { background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: 8, padding: "8px 14px", color: colors.inputText, fontSize: 13, outline: "none", minWidth: 220 },
    select: { background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: 8, padding: "8px 14px", color: colors.inputText, fontSize: 13, outline: "none", cursor: "pointer" },
    table: { width: "100%", borderCollapse: "collapse" as const },
    th: { fontSize: 13, color: colors.text, fontWeight: 600, letterSpacing: 0.8, padding: "10px 16px", textAlign: "left" as const, borderBottom: `1px solid ${colors.border}`, cursor: "pointer", userSelect: "none" as const },
    td: { padding: "14px 16px", borderBottom: `1px solid ${colors.borderAlt}`, verticalAlign: "middle" },
    card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, overflow: "hidden" },
  };

  const sortHeader = (key: string, label: string) => (
    <th style={{ ...styles.th, color: sortBy === key ? "#6C63FF" : colors.text }}
      onClick={() => setSortBy(key)}>{label} {sortBy === key ? "↓" : ""}</th>
  );

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.text, margin: 0 }}>All Reps</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: "3px 0 0" }}>{filtered.length} members across all tenants</p>
        </div>
      </div>

      {/* Summary Cards */}
      {/* <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Total Members", value: userData?.data?.tenantUserList?.items?.length || 0, color: "#6C63FF" },
          { label: "With Pitches", value: reps.filter((r: any) => r.pitchCount > 0).length, color: "#4ADE80" },
          { label: "With Practice", value: reps.filter((r: any) => r.practiceSessionCount > 0).length, color: "#60A5FA" },
          { label: "Avg Score", value: Math.round(avg(reps.filter((r: any) => r.avgScore > 0).map((r: any) => r.avgScore))) || "—", color: "#FACC15" },
        ].map((c) => (
          <div key={c.label} style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 0.5, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div> */}

      {/* Controls */}
      <div style={styles.controls}>
        <input
          style={styles.input}
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={styles.select} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option style={{ background: colors.inputBg, color: colors.inputText }} value="ALL">All Roles</option>
          <option style={{ background: colors.inputBg, color: colors.inputText }} value="SUPER_ADMIN">Super Admin</option>
          <option style={{ background: colors.inputBg, color: colors.inputText }} value="ADMIN">Admin</option>
          <option style={{ background: colors.inputBg, color: colors.inputText }} value="MANAGER">Manager</option>
          <option style={{ background: colors.inputBg, color: colors.inputText }} value="USER">User</option>
        </select>
      </div>

      {/* Table */}
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              {sortHeader("name", "REP")}
              <th style={styles.th}>ROLE</th>
              <th style={styles.th}>EMAIL</th>
              {sortHeader("score", "AVG SCORE")}
              {sortHeader("pitches", "ALL SESSIONS")}
              {sortHeader("practice", "PRACTICE SESSIONS")}
              <th style={styles.th}>STATUS</th>
              <th style={{ ...styles.th, cursor: "default", width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((rep: any, i: number) => (
              <tr key={rep.userId}
                style={{
                  background: hoveredRow === rep.userId
                    ? colors.rowHoverAlt
                    : i % 2 === 0 ? "transparent" : colors.surfaceAlt,
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredRow(rep.userId)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => onRepClick && onRepClick(rep)}
              >
                <td style={styles.td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={rep.name} size={34} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{rep.name}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}><RoleBadge role={rep.role} /></td>
                <td style={styles.td}><span style={{ fontSize: 14, color: colors.text }}>{rep.email}</span></td>
                <td style={styles.td}><ScoreBadge score={rep.avgScore} /></td>
                <td style={styles.td}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: rep.pitchCount > 0 ? colors.text : colors.textMutedLight }}>
                    {rep.pitchCount}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 22 }}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <div key={j} style={{
                          width: 8, height: 22 - j * 4, borderRadius: 2,
                          background: j < (rep.practiceSessionCount || 0) ? "#6C63FF" : colors.divider,
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 12, color: colors.textMuted }}>{rep.practiceSessionCount}</span>
                  </div>
                </td>
                <td style={styles.td}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
                    background: (rep.status === "active" || rep.status === true) ? "rgba(74,222,128,0.12)" : colors.badgeBg,
                    color: (rep.status === "active" || rep.status === true) ? "#4ADE80" : colors.textMuted,
                  }}>
                    {(rep.status === "active" || rep.status === true) ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={styles.td}>
                  <ArrowBtn
                    onClick={() => onRepClick && onRepClick(rep)}
                    hover={hoveredRow === rep.userId}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: "rgba(255,255,255,0.3)", fontSize:13 }}>
            No reps match your filters
          </div>
        )}
      </div>
    </div>
  );
}
